import { useCallback, useState } from 'react'
import { toast } from 'react-common'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { v4 as uuid } from 'uuid'
import { CreateSelfEnrollmentsApi } from '@/api/models/createSelfEnrollments'
import { useCourseNavigate } from '@/hooks/useCourseNavigate'
import { useGetObjectiveAccessByIdQuery } from '@/store/api/aggregatesApi'
import { useAppDispatch } from '@/store/hooks'
import { showcasesApi } from '../../../../store/api/showcasesApi'

export const useSelfEnrollment = () => {
  const { t } = useTranslation()

  const { objectiveId = '' } = useParams()
  const { navigateToContent } = useCourseNavigate()

  const { data: aggregate, refetch: refetchAggregate } = useGetObjectiveAccessByIdQuery(objectiveId, {
    skip: !objectiveId,
  })

  const isSelfEnrolled = aggregate?.objectiveWorkflowAggregate?.isSelfEnrolled

  const [isSelfEnrollmentLoading, setIsSelfEnrollmentLoading] = useState(false)

  const dispatch = useAppDispatch()
  const createSelfEnrollment = useCallback(async () => {
    if (isSelfEnrolled) {
      return
    }

    try {
      setIsSelfEnrollmentLoading(true)

      const entityId = uuid()
      await CreateSelfEnrollmentsApi.create({ id: entityId, objectiveAccessId: aggregate?.id! })

      const response = await refetchAggregate()

      toast.success(t('courseDetails.courseSelfEnrolled', { courseName: aggregate!.objective!.name }))

      /**
       * after self enrollment creation, we need to clean showcases state to re-fetch enrolled/catalog showcases.
       */
      dispatch(showcasesApi.util.resetApiState())

      return response?.data
    } catch (e) {
      console.log(e)
    } finally {
      setIsSelfEnrollmentLoading(false)
    }
  }, [aggregate, isSelfEnrolled, refetchAggregate, t, dispatch])

  const startCourseWithSelfEnrollment = useCallback(
    async (
      startWorkflow: (isRetakeClick?: boolean, objectiveWorkflowAggregateIdParam?: string) => Promise<unknown>,
    ) => {
      try {
        setIsSelfEnrollmentLoading(true)
        const objectiveAccess = await createSelfEnrollment()

        await startWorkflow(false, objectiveAccess?.objectiveWorkflowAggregate?.id)
        await refetchAggregate()

        navigateToContent(objectiveId!)
      } finally {
        setIsSelfEnrollmentLoading(false)
      }
    },
    [createSelfEnrollment, navigateToContent, objectiveId, refetchAggregate],
  )

  return {
    createSelfEnrollment,
    startCourseWithSelfEnrollment,

    isSelfEnrolled,
    isSelfEnrollmentLoading,
  }
}
